import { JobProgressStatus } from 'const/JobProgress';
import JobProgressService from 'services/job-progress/JobProgress';

import useTimeout from './useTimeout';

import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useRef } from 'react';
import useStateAsync from 'hooks/useStateAsync';
import { StatusLevel } from 'const/StatusLevel';

const createJobPollingUtility = (userOptions = {}) => {
  const defaultOptions = {
    fetchRequestIdParam: (data) => {
      return data.requestId;
    },
    progressCallback: () => {},
    interval: 5000,
  };

  const options = { ...defaultOptions, ...userOptions };

  const sleep = (delay) => new Promise((resolve) => setTimeout(resolve, delay));

  const getJobProgress = async (requestId) => {
    const { data } = await JobProgressService.getJobProgress(requestId);
    return data;
  };

  const isValid = (data) => {
    if (!data) {
      throw new Error('Theres no job progress data');
    }
    const { status } = data;
    if (
      status === JobProgressStatus.Failed ||
      status === JobProgressStatus.Failure ||
      status === JobProgressStatus.Error
    ) {
      const error = new Error(data?.infoData ?? 'Job has failed');
      error.data = data; // carry the original payload through
      throw error;
    }
    return (
      status === JobProgressStatus.Finished ||
      status === JobProgressStatus.Success ||
      status === JobProgressStatus.Partially_Finished
    );
  };

  const repeatJobProgress = async (requestId) => {
    if (!requestId) {
      throw new Error('No requestId provided');
    }

    while (true) {
      const data = await getJobProgress(requestId);
      options.progressCallback(data);

      if (isValid(data)) {
        await sleep(1000);
        return data;
      }

      await sleep(options.interval);
    }
  };

  const runJobWithPolling = async (data) => {
    return repeatJobProgress(options.fetchRequestIdParam(data));
  };

  const run = async (data) => {
    return runJobWithPolling(data);
  };

  return {
    run,
  };
};

const useWarningTimeout = (show = true) => {
  const seconds = 10 * 1000;
  const { t } = useTranslation();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const snackbarKey = useRef(null);

  const timeout = show ? seconds : null;
  useTimeout(() => {
    if (show) {
      snackbarKey.current = enqueueSnackbar(t('views.onechip_cards.actions.general_message'), {
        variant: 'warning',
      });
    }
  }, timeout);

  useEffect(() => {
    return () => {
      closeSnackbar(snackbarKey.current);
    };
  }, []);
};

const useJobProgressTracker = (service, userOptions = {}) => {
  const { enqueueSnackbar } = useSnackbar();

  const defaultOptions = {
    progressCallback: () => {},
    afterJobServices: () => [],
    interval: 5000,
  };

  const options = { ...defaultOptions, ...userOptions };

  const { onSuccess = null, onComplete = null, onError = null, ...pollingOptions } = options;

  const serviceListToRun = useMemo(() => {
    const pollingUtility = createJobPollingUtility(pollingOptions);
    const functions = [service, pollingUtility.run, ...pollingOptions.afterJobServices(null)];
    return functions;
  }, [service]);

  const job = useStateAsync(serviceListToRun, {
    immediate: false,
    retry: true,
    retriesCount: 5,
    onComplete: ({ data, error }) => {
      const effectiveData = data ?? error?.data;
      const isWarning = effectiveData?.statusLevel === StatusLevel.WARNING;

      if (isWarning) {
        enqueueSnackbar(effectiveData.infoData ?? effectiveData?.errorData, {
          variant: 'warning',
        });
      }
      onComplete?.();
    },
    shouldRetry: (statusCode) => {
      return statusCode === 500;
    },
  });
  // useWarningTimeout(job.isLoading);

  const isWarning = (job.data ?? job.error?.data)?.statusLevel === StatusLevel.WARNING;

  return { ...job, isWarning };
};

export default useJobProgressTracker;
